import { BaseExternalOperation } from "@/store/sync/operations/BaseExternalOperation";
import {
  ExternalOperationKind,
  IUploadImageExternalOperation,
} from "@/store/sync/operations/types";
import { fileModule } from "@/modules/file";
import { MemCommonEditorImageInfo } from "@mem-labs/common-editor";
import { logger } from "@/modules/logger";
import { toastModule } from "@/modules/toast";
import { resolveNoteAttachmentUuid } from "@/store/note-attachments/resolveAttachmentUuid";
import pRetry from "p-retry";

// Total upload attempts before failing terminally (pRetry `retries` + the first
// try). Kept small: the failure UI ("Image can't be accessed") is the same
// either way, and the user can always re-add the image.
const MAX_UPLOAD_ATTEMPTS = 5;

export class UploadImageExternalOperation extends BaseExternalOperation<IUploadImageExternalOperation> {
  triggerRecompute(): Promise<void> {
    return Promise.resolve();
  }

  get operationKind(): ExternalOperationKind.UPLOAD_IMAGE {
    return ExternalOperationKind.UPLOAD_IMAGE;
  }

  /**
   * Skipping this op removes it and reverts its optimistic attachment row, but
   * the bytes cached as `pending` at insert would be orphaned — reconcile never
   * drops pending (normally the only copy), so nothing else would collect them.
   * Evict them here, since the op that would have uploaded them is gone.
   * Best-effort; only runs in the full app (guests have no blob store).
   */
  async onSkipped(): Promise<void> {
    if (!("localBlobStore" in this.store)) return;
    const { noteId, info } = this.payload;
    if (noteId === undefined || !info.downloadUrl) return;
    const attachmentId = resolveNoteAttachmentUuid(noteId, info.downloadUrl);
    // Clear the in-flight marker too: it's set synchronously at insert and lives
    // in an in-memory set that evict() doesn't touch, so without this the
    // resolver keeps rendering "Uploading…" all session for an abandoned upload.
    this.store.localBlobStore.clearUploadInFlight(attachmentId);
    await this.store.localBlobStore.evict(attachmentId);
  }

  async executeExternalOperation(): Promise<void> {
    const info: MemCommonEditorImageInfo = this.payload.info;

    // The optimistic PENDING row, the pending-byte cache, and the "Uploading…"
    // marker are all written at INSERT time
    // (NoteObservable.uploadImageAssociatedWithNote), before this op is queued —
    // the sync queue blocks this op on reconnect, so doing them here would mean
    // nothing renders offline. This op owns only the network half:
    // upload → READY, draining whenever the queue next runs.
    // `noteId` was added to this op's persisted payload. Ops queued by a previous
    // deploy replay with `noteId === undefined`; without it we can't resolve the
    // attachment uuid, so fall back to legacy upload (no LBS / no hosting_status).
    const canWriteHostingStatus = this.payload.noteId !== undefined;
    // Cache key == associated_note_attachment_id; for an upload the identity URL
    // (info.downloadUrl) is the GCS serving_url, so the id resolves the same way.
    const attachmentId =
      canWriteHostingStatus && info.downloadUrl
        ? resolveNoteAttachmentUuid(this.payload.noteId, info.downloadUrl)
        : undefined;

    const canBeUploaded = fileModule.checkIfImageCanBeUploaded({ info });

    if (!canBeUploaded) {
      /**
       * Defensive backstop only — mem-common-editor rejects oversize images
       * (ImageRejected modal) before ImageAdded fires, so this ~never trips. The
       * insert path already created the row; mark it FAILED/TOO_LARGE here.
       * Exit early (no throw/retry) — loading an over-large file into memory is
       * what we're guarding against.
       */
      logger.error({
        message: `[SYNC][UploadImageExternalOperation] Image cannot be uploaded. imageId=${info.imageId} imageSizeBytes=${info.imageSizeBytes}. Exiting early.`,
      });

      if (attachmentId) {
        // Evict the bytes putPending wrote at insert: this path returns normally
        // (no throw), so onSkipped never fires, and pending blobs are
        // eviction-immune + rehydrate on init — without this the blob leaks
        // permanently across reloads and keeps isLocalUpload true.
        this.store.localBlobStore.clearUploadInFlight(attachmentId);
        await this.store.localBlobStore.evict(attachmentId);
        await this.store.spaceAccountNoteAttachments.writeHostingStatusOptimistically({
          attachmentId,
          hostingStatus: "FAILED",
          hostingFailureReason: "TOO_LARGE",
        });
      }

      toastModule.triggerToast({ content: "Failed to upload image - too large." });

      return;
    }

    const fileBlob = fileModule.convertEncodedFileContentToBlob({
      encodedFileContent: info.encodedImageContent,
      fileMimeType: info.imageMimeType,
    });

    try {
      // Retry the upload itself (bounded, exponential backoff owned by pRetry).
      // Retrying only the network call — not the surrounding FAILED-write —
      // keeps the failure handling below single-shot. This sits inside the
      // queue's own retry loop, but because exhaustion resolves to a clean
      // `return` (not a throw), the outer loop sees success and never re-runs.
      await pRetry(
        () =>
          fileModule.upload({
            info: {
              fileId: info.imageId,
              fileMimeType: info.imageMimeType,
              normalizedFileName: info.normalizedImageName,
              encodedFileContent: info.encodedImageContent,
              fileSizeBytes: info.imageSizeBytes,
            },
            blob: fileBlob,
          }),
        {
          retries: MAX_UPLOAD_ATTEMPTS - 1,
          onFailedAttempt: attemptError => {
            logger.error({
              message: "[SYNC][UploadImageExternalOperation] Image upload attempt failed",
              info: {
                imageId: info.imageId,
                attempt: attemptError.attemptNumber,
                retriesLeft: attemptError.retriesLeft,
                error: String(attemptError),
              },
            });
          },
        }
      );
    } catch (error) {
      // All upload attempts exhausted → fail cleanly: mark the image FAILED (the
      // resolver shows the "Image can't be accessed" node) and terminate the op
      // by returning (do NOT throw — a raw throw would route into the generic
      // "Unable to upload image…" modal and re-trigger on every resume). The
      // FAILED hosting_status is the single, correct failure UI; the user can
      // re-add the image to retry.
      logger.error({
        message: "[SYNC][UploadImageExternalOperation] Image upload failed",
        info: { imageId: info.imageId, error: String(error) },
      });
      if (attachmentId) {
        this.store.localBlobStore.clearUploadInFlight(attachmentId);
        // Evict the pending bytes: this path returns (no throw), so onSkipped
        // never fires, and pending blobs are eviction-immune + rehydrate on
        // init — without this the blob would leak permanently across reloads
        // and keep isLocalUpload true. (Same reasoning as the too-large path.)
        await this.store.localBlobStore.evict(attachmentId);
        await this.store.spaceAccountNoteAttachments.writeHostingStatusOptimistically({
          attachmentId,
          hostingStatus: "FAILED",
          hostingFailureReason: "FETCH_OR_TRANSFORM_FAILED",
        });
      }
      return;
    }

    if (attachmentId && info.downloadUrl) {
      const safeAttachmentId = attachmentId;
      try {
        // Write READY first, then clear the local-upload signals. Clearing them
        // makes isLocalUpload false; doing that while hosting_status were still
        // PENDING would briefly flip the resolver to the external shimmer.
        // READY-first means the resolver sees READY (or the blob) before
        // isLocalUpload drops.
        await this.store.spaceAccountNoteAttachments.writeHostingStatusOptimistically({
          attachmentId: safeAttachmentId,
          hostingStatus: "READY",
          servingUrl: info.downloadUrl,
        });
        // markUploaded is a no-op if putPending was refused (online cache-full).
        await this.store.localBlobStore.markUploaded(safeAttachmentId);
      } finally {
        // Always clear in-flight, even if markUploaded threw — otherwise the
        // marker dangles and the image sticks on "Uploading…".
        this.store.localBlobStore.clearUploadInFlight(safeAttachmentId);
      }
    }
  }
}
